/**
 * EngageProspectButton — Module 10 trigger from a prospect drawer.
 *
 * Renders a CTA + modal that:
 *   1. POST /prospect/:id/engagement-link → returns a magic deep_link
 *   2. Shows the link + Copy button + share helpers (Instagram DM, SMS, mail)
 *   3. Lists previously generated links with their status
 *
 * Drop into any prospect detail view as:
 *   <EngageProspectButton prospectId={id} prospectName={name} />
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send, Copy, CheckCircle2, ExternalLink, X, Trash2 } from 'lucide-react';
import {
  useCreateEngagementLink,
  useProspectEngagementLinks,
  useRevokeEngagementLink,
} from '@/hooks/useConversations';

interface Props {
  prospectId: string;
  prospectName: string | null;
}

export function EngageProspectButton({ prospectId, prospectName }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="mb-3 flex w-full items-center justify-between gap-3 rounded-2xl border border-violet-300/40 bg-gradient-to-br from-violet-50 to-pink-50 px-4 py-3.5 text-left transition hover:from-violet-100 hover:to-pink-100"
      >
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white">
            <Send size={15} />
          </span>
          <span>
            <span className="font-display text-[14px] font-800 tracking-[-0.015em]">
              {t('engage.cta', { name: prospectName ?? t('engage.thisProspect') })}
            </span>
            <p className="mt-0.5 text-[12px] text-ink/55">
              {t('engage.ctaSub')}
            </p>
          </span>
        </span>
        <span className="font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-violet-600">
          INEA
        </span>
      </button>
      {open && (
        <EngagementModal
          prospectId={prospectId}
          prospectName={prospectName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function EngagementModal({
  prospectId,
  prospectName,
  onClose,
}: {
  prospectId: string;
  prospectName: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: links, isLoading } = useProspectEngagementLinks(prospectId);
  const create = useCreateEngagementLink();
  const revoke = useRevokeEngagementLink();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 px-4">
      <div className="card w-full max-w-lg space-y-4 p-5">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-700 text-ink">
              {t('engage.modalTitle', { name: prospectName ?? t('engage.thisProspect') })}
            </h3>
            <p className="mt-0.5 text-[12px] text-ink/55">
              {t('engage.modalSub')}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-ink/60 hover:bg-ink/5">
            <X size={16} />
          </button>
        </header>

        <button
          onClick={() => create.mutate({ prospectId })}
          disabled={create.isPending}
          className="hero-cta-light-primary inline-flex w-full items-center justify-center gap-2 disabled:opacity-50"
        >
          {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {t('engage.generate')}
        </button>

        {create.isError && (
          <p className="text-[11px] text-red-600">
            {create.error?.message ?? t('engage.generateError')}
          </p>
        )}

        {/* Hero copy card — appears after a fresh link is generated, makes
            the next obvious action (copy + share) impossible to miss. */}
        {create.isSuccess && create.data && (
          <FreshLinkHero
            link={create.data.deep_link}
            onCopied={() => copy(create.data!.deep_link, `fresh-${create.data!.id}`)}
            justCopied={copiedId === `fresh-${create.data.id}`}
          />
        )}

        <div className="border-t border-ink/10 pt-3">
          <p className="mb-2 text-[11px] font-700 uppercase tracking-wide text-ink/55">
            {t('engage.existingLinks')}
          </p>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="animate-spin text-ink/50" size={16} />
            </div>
          ) : !links || links.length === 0 ? (
            <p className="text-[12px] text-ink/55">{t('engage.noLinks')}</p>
          ) : (
            <ul className="space-y-2">
              {links.map((l) => (
                <li
                  key={l.id}
                  className="flex items-center gap-2 rounded-lg border border-ink/10 bg-white px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-[11px] font-700 text-ink/75">
                      <StatusPill status={l.status} />
                      {l.click_count > 0 && (
                        <span className="text-ink/55">· {t('engage.clicks', { count: l.click_count })}</span>
                      )}
                    </p>
                    <p className="truncate font-mono text-[11px] text-ink/65">{l.deep_link}</p>
                  </div>
                  <button
                    onClick={() => copy(l.deep_link, l.id)}
                    className="rounded-lg border border-ink/10 bg-white p-1.5 text-ink/65 hover:bg-ink/5"
                    title={t('engage.copy')}
                  >
                    {copiedId === l.id ? <CheckCircle2 size={12} className="text-green-600" /> : <Copy size={12} />}
                  </button>
                  <a
                    href={l.deep_link}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border border-ink/10 bg-white p-1.5 text-ink/65 hover:bg-ink/5"
                    title={t('engage.test')}
                  >
                    <ExternalLink size={12} />
                  </a>
                  {l.status === 'pending' && (
                    <button
                      onClick={() => revoke.mutate({ linkId: l.id, prospectId })}
                      className="rounded-lg border border-red-200 bg-red-50 p-1.5 text-red-600 hover:bg-red-100"
                      title={t('engage.revoke')}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: t('engage.status.pending'), cls: 'bg-violet-100 text-violet-700' },
    consumed: { label: t('engage.status.consumed'), cls: 'bg-green-100 text-green-700' },
    expired: { label: t('engage.status.expired'), cls: 'bg-slate-100 text-slate-600' },
    revoked: { label: t('engage.status.revoked'), cls: 'bg-red-100 text-red-700' },
  };
  const entry = map[status] ?? { label: status, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-700 ${entry.cls}`}>
      {entry.label}
    </span>
  );
}

/**
 * Hero card displayed right after a fresh engagement link is created.
 * The partner just clicked "Générer" — make the next obvious action
 * (copy + paste to prospect) one giant tappable button.
 */
function FreshLinkHero({
  link,
  onCopied,
  justCopied,
}: {
  link: string;
  onCopied: () => void;
  justCopied: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-pink-50 p-4">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-700 uppercase tracking-wide text-violet-700">
        <CheckCircle2 size={12} /> {t('engage.linkReady')}
      </p>
      <p className="mb-3 break-all font-mono text-[11px] text-ink/70">{link}</p>
      <button
        onClick={onCopied}
        className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-700 transition ${
          justCopied
            ? 'bg-green-500 text-white'
            : 'bg-ink text-white hover:bg-ink/90'
        }`}
      >
        {justCopied ? (
          <>
            <CheckCircle2 size={16} /> {t('engage.copied')}
          </>
        ) : (
          <>
            <Copy size={16} /> {t('engage.copyLink')}
          </>
        )}
      </button>
      <p className="mt-2 text-center text-[11px] text-ink/55">
        {t('engage.pasteHint')}
      </p>
    </div>
  );
}
